import { useMemo } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./select";
import { cn } from "../../utils";

/**
 * Hour/minute picker built on the themed Select.
 *
 * `<input type="time">` renders the browser's own dropdown, which follows the OS
 * light/dark setting and ignores the app's themes entirely — a white panel over a
 * dark page. This keeps the same "HH:MM" value contract so callers are unchanged.
 */

const HOURS = Array.from({ length: 24 }, (_, h) => String(h).padStart(2, "0"));

export interface TimePickerProps {
  /** "HH:MM" — the same shape `<input type="time">` produces. */
  value: string;
  onChange: (value: string) => void;
  /** Minute granularity; 5 keeps the list scannable for trading sessions. */
  minuteStep?: number;
  hourAriaLabel?: string;
  minuteAriaLabel?: string;
  disabled?: boolean;
  className?: string;
}

/** Tolerates "9:5", "09:30:00" and empty values rather than rendering blank. */
export function parseTime(value: string): { hour: string; minute: string } {
  const [rawHour = "", rawMinute = ""] = (value ?? "").split(":");

  // Digits are required: Number("") is 0, which would render an unset field as
  // midnight and let the user save a time they never chose.
  const toPart = (raw: string, max: number) => {
    if (!/^\d{1,2}$/.test(raw.trim())) return "";
    const n = Number(raw);
    return n >= 0 && n < max ? String(n).padStart(2, "0") : "";
  };

  return { hour: toPart(rawHour, 24), minute: toPart(rawMinute, 60) };
}

/**
 * Minute options for the step, always including `keepMinute` if it falls off the
 * grid — an existing 09:33 must stay selectable instead of silently resetting.
 */
export function buildMinuteOptions(minuteStep: number, keepMinute?: string): string[] {
  const step = minuteStep > 0 && minuteStep <= 60 ? minuteStep : 5;
  const list = Array.from({ length: Math.ceil(60 / step) }, (_, i) =>
    String(i * step).padStart(2, "0")
  );
  if (keepMinute && !list.includes(keepMinute)) {
    list.push(keepMinute);
    list.sort();
  }
  return list;
}

export function TimePicker({
  value,
  onChange,
  minuteStep = 5,
  hourAriaLabel,
  minuteAriaLabel,
  disabled,
  className,
}: TimePickerProps) {
  const { hour, minute } = parseTime(value);

  const minutes = useMemo(
    () => buildMinuteOptions(minuteStep, minute),
    [minuteStep, minute]
  );

  // Editing one half must not wipe the other: default the missing side to "00".
  const emit = (nextHour: string, nextMinute: string) =>
    onChange(`${nextHour || "00"}:${nextMinute || "00"}`);

  return (
    <div className={cn("flex items-center gap-1", className)}>
      <Select value={hour} onValueChange={(h) => emit(h, minute)} disabled={disabled}>
        <SelectTrigger className="h-8 flex-1 font-mono" aria-label={hourAriaLabel}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent className="max-h-60">
          {HOURS.map((h) => (
            <SelectItem key={h} value={h} className="font-mono">
              {h}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <span className="text-muted-foreground text-sm" aria-hidden>
        :
      </span>

      <Select value={minute} onValueChange={(m) => emit(hour, m)} disabled={disabled}>
        <SelectTrigger className="h-8 flex-1 font-mono" aria-label={minuteAriaLabel}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent className="max-h-60">
          {minutes.map((m) => (
            <SelectItem key={m} value={m} className="font-mono">
              {m}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

export default TimePicker;
